import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  IconButton,
  Divider,
  TextField,
  Tabs,
  Tab,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import HomeIcon from '@mui/icons-material/Home';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import LanguageIcon from '@mui/icons-material/Language';
import PersonIcon from '@mui/icons-material/Person';
import LoginIcon from '@mui/icons-material/Login';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useTranslation } from 'react-i18next';
import { LANGUAGE_LABELS } from '../../utils/languages';
import { useUserStore } from '../../store';
import Tooltip from '@mui/material/Tooltip';
import StartInvoiceSubscriptionForm from './StartInvoiceSubscriptionForm';
import { formatPriceWithCurrency } from '../../utils/vat';
import CancelSubscriptionPanel from './CancelSubscriptionPanel';
import DataPurgePanel from './DataPurgePanel';
import FeatureFlagsPanel from './FeatureFlagsPanel';

function InfoItem({ label, value, icon, xs = 12, sm = 6 }) {
  return (
    <Grid item xs={xs} sm={sm}>
      <Paper variant="outlined" sx={{ p: 1.5, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          {icon && <Box sx={{ color: 'text.disabled', flexShrink: 0 }}>{icon}</Box>}
          <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
            <Typography variant="caption" color="text.secondary" display="block">
              {label}
            </Typography>
            <Typography
              variant="body2"
              fontWeight={500}
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', wordBreak: 'break-word' }}
            >
              {value || '-'}
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Grid>
  );
}

function FeatureChip({ label, enabled, t }) {
  return (
    <Chip
      icon={enabled ? <CheckCircleIcon /> : <CancelIcon />}
      label={label}
      size="small"
      color={enabled ? 'success' : 'default'}
      variant={enabled ? 'filled' : 'outlined'}
      sx={{ fontWeight: 500, fontSize: '12px' }}
    />
  );
}

const STATUS_COLORS = {
  active: 'success',
  suspended: 'error',
  cancelled: 'error',
  none: 'default',
};

const ROLE_ORDER = { admin: 0, foreman: 1, employee: 2, accountant: 3 };

export default function OrganizationDetailDialog({ open, onClose, organizationId }) {
  const { t } = useTranslation();
  const { user: currentUser } = useUserStore();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [extendTrialDate, setExtendTrialDate] = useState('');
  const [extendTrialLoading, setExtendTrialLoading] = useState(false);
  const [extendTrialSuccess, setExtendTrialSuccess] = useState(null);
  const [extendTrialError, setExtendTrialError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (!open || !organizationId) return;
    setLoading(true);
    setError(null);
    setExtendTrialDate('');
    setExtendTrialSuccess(null);
    setExtendTrialError(null);
    setActiveTab(0);
    axios.get(`/api/vadmin/organizations/${organizationId}/`)
      .then((res) => {
        setOrg(res.data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.response?.data?.detail || err.message);
        setLoading(false);
      });
  }, [open, organizationId]);

  const refreshOrg = async () => {
    const res = await axios.get(`/api/vadmin/organizations/${organizationId}/`);
    setOrg(res.data);
  };

  // Invoice billing only (plan_price is null for Stripe): the next plan fee, with any
  // percentage discount and its last day folded in. Shape: payments.services
  // .subscription_pricing.PlanPrice.as_dict().
  const formatPlanPrice = (pp) => {
    const unit = pp.unit === 'vuosi' ? t('vadmin.perYear') : t('vadmin.perMonth');
    const price = formatPriceWithCurrency(pp.price);
    if (!pp.discount_active) return t('vadmin.planPricePlain', { price, unit });
    const percent = String(Number(pp.discount_percent)).replace('.', ',');
    if (!pp.discount_last_day) return t('vadmin.planPriceWithDiscountForever', { price, unit, percent });
    return t('vadmin.planPriceWithDiscount', { price, unit, percent, date: formatDate(pp.discount_last_day) });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return t('vadmin.never');
    return new Date(dateStr).toLocaleDateString('fi-FI');
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return t('vadmin.never');
    return new Date(dateStr).toLocaleString('fi-FI');
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: t('vadmin.roleAdmin'),
      foreman: t('vadmin.roleForeman'),
      employee: t('vadmin.roleEmployee'),
      accountant: t('vadmin.roleAccountant'),
    };
    return labels[role] || role;
  };

  const getRoleColor = (role) => {
    if (role === 'admin') return 'error';
    if (role === 'foreman') return 'warning';
    if (role === 'accountant') return 'info';
    return 'default';
  };

  const getStatusLabel = (status, isTrial) => {
    if (status === 'active' && isTrial) return t('vadmin.trial');
    const labels = {
      active: t('vadmin.active'),
      suspended: t('vadmin.suspended'),
      cancelled: t('vadmin.cancelled'),
      none: t('vadmin.noSubscription'),
    };
    return labels[status] || status;
  };

  const getStatusColor = (status, isTrial) => {
    if (status === 'active' && isTrial) return 'warning';
    return STATUS_COLORS[status] || 'default';
  };

  const canExtendTrial = org && org.is_trial && !org.trial_converted;

  // Cancellation scheduled but not yet in effect (subscription_end is exclusive).
  const hasPendingCancellation =
    !!org?.subscription_end && new Date(org.subscription_end) > new Date();

  const handleExtendTrial = async () => {
    if (!extendTrialDate) return;
    setExtendTrialLoading(true);
    setExtendTrialError(null);
    setExtendTrialSuccess(null);
    try {
      const res = await axios.post(
        `/api/vadmin/organizations/${organizationId}/extend-trial/`,
        { new_trial_end: extendTrialDate }
      );
      setExtendTrialSuccess(res.data.detail || t('vadmin.extendTrialSuccess'));
      await refreshOrg();
      setExtendTrialDate('');
    } catch (err) {
      setExtendTrialError(err.response?.data?.detail || err.message);
    } finally {
      setExtendTrialLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: '50vh' } }}
    >
      <DialogTitle sx={{ pr: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '10px',
              bgcolor: 'rgba(49, 130, 206, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'secondary.main',
              flexShrink: 0,
            }}
          >
            <BusinessIcon />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h6" fontWeight={600} noWrap>
              {org?.name || '...'}
            </Typography>
            {org?.business_id && (
              <Typography variant="body2" color="text.secondary">
                {org.business_id}
              </Typography>
            )}
          </Box>
          {org && (
            <Chip
              label={getStatusLabel(org.subscription_status, org.is_trial)}
              color={getStatusColor(org.subscription_status, org.is_trial)}
              size="small"
              variant={org.subscription_status === 'none' ? 'outlined' : 'filled'}
              sx={{ fontWeight: 500, flexShrink: 0 }}
            />
          )}
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {org && !loading && (
          <>
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label={t('vadmin.tabOverview')} />
              <Tab label={t('vadmin.tabSubscription')} />
              <Tab label={t('vadmin.tabFeatures')} data-testid="vadmin-tab-features" />
            </Tabs>

            {activeTab === 0 && (
            <Box>
            {/* Contact & Address */}
            <Typography variant="subtitle2" sx={{ mb: 1.5, mt: 1, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'text.secondary', fontSize: '12px' }}>
              {t('vadmin.contactInfo')}
            </Typography>
            <Grid container spacing={1.5} sx={{ mb: 3 }}>
              <InfoItem label={t('vadmin.contactEmail')} value={org.contact_email} icon={<EmailIcon fontSize="small" />} />
              <InfoItem label={t('vadmin.invoiceEmail')} value={org.invoice_email} icon={<EmailIcon fontSize="small" />} />
              <InfoItem label={t('Phone')} value={org.phone} icon={<PhoneIcon fontSize="small" />} />
              <InfoItem label={t('vadmin.accountantEmail')} value={org.accountant_email} icon={<EmailIcon fontSize="small" />} />
              <InfoItem label={t('vadmin.address')} value={org.address_formatted} icon={<HomeIcon fontSize="small" />} xs={12} sm={12} />
            </Grid>

            {/* Subscription */}
            <Typography variant="subtitle2" sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'text.secondary', fontSize: '12px' }}>
              {t('vadmin.subscriptionInfo')}
            </Typography>
            <Grid container spacing={1.5} sx={{ mb: 3 }}>
              <InfoItem label={t('vadmin.plan')} value={org.plan_name} />
              <InfoItem label={t('vadmin.billing')} value={org.billing_period === 'monthly' ? t('vadmin.monthly') : org.billing_period === 'yearly' ? t('vadmin.yearly') : null} />
              <InfoItem label={t('vadmin.paymentMethod')} value={org.payment_method === 'invoice' ? t('vadmin.invoice') : org.payment_method === 'stripe' ? 'Stripe' : null} />
              <InfoItem label={t('vadmin.seatLimit')} value={org.seat_limit === 0 ? t('vadmin.unlimited') : `${org.user_count} / ${org.seat_limit}`} />
              {org.plan_price && (
                <InfoItem
                  label={t('vadmin.planPrice')}
                  value={formatPlanPrice(org.plan_price)}
                />
              )}
              {org.is_trial && org.trial_end && (
                <InfoItem label={t('vadmin.trialEnds')} value={`${formatDateTime(org.trial_end)} (${org.trial_days_remaining} ${t('vadmin.trialDaysLeft', { days: org.trial_days_remaining }).split(' ').slice(1).join(' ')})`} />
              )}
              {org.current_period_end && (
                <InfoItem label={t('vadmin.periodEnd')} value={formatDateTime(org.current_period_end)} />
              )}
              {org.last_access_date && (
                <InfoItem
                  label={t('vadmin.lastAccessDate')}
                  value={formatDate(org.last_access_date)}
                />
              )}
            </Grid>

            {hasPendingCancellation && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                {t('vadmin.cancelSubScheduledFor', { date: formatDate(org.last_access_date) })}
              </Alert>
            )}

            {canExtendTrial && (
              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                  {t('vadmin.extendTrial')}
                </Typography>
                {extendTrialSuccess && (
                  <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setExtendTrialSuccess(null)}>
                    {extendTrialSuccess}
                  </Alert>
                )}
                {extendTrialError && (
                  <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setExtendTrialError(null)}>
                    {extendTrialError}
                  </Alert>
                )}
                {org.subscription_status === 'suspended' && (
                  <Alert severity="info" sx={{ mb: 1.5 }}>
                    {t('vadmin.extendTrialReactivateNote')}
                  </Alert>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <TextField
                    type="date"
                    size="small"
                    label={t('vadmin.newTrialEnd')}
                    value={extendTrialDate}
                    onChange={(e) => setExtendTrialDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      min: new Date(Date.now() + 86400000).toISOString().split('T')[0],
                    }}
                    sx={{ width: 200 }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleExtendTrial}
                    disabled={!extendTrialDate || extendTrialLoading}
                  >
                    {extendTrialLoading ? <CircularProgress size={20} /> : t('vadmin.extendTrialButton')}
                  </Button>
                </Box>
              </Paper>
            )}

            {/* Banking */}
            <Typography variant="subtitle2" sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'text.secondary', fontSize: '12px' }}>
              {t('vadmin.bankingInfo')}
            </Typography>
            <Grid container spacing={1.5} sx={{ mb: 3 }}>
              <InfoItem label="IBAN" value={org.iban} icon={<AccountBalanceIcon fontSize="small" />} />
              <InfoItem label="BIC" value={org.bic} />
              <InfoItem label={t('vadmin.vatNumber')} value={org.vat_number} />
              <InfoItem label={t('vadmin.paymentTerms')} value={org.default_payment_terms_days ? t('vadmin.paymentTermsDays', { days: org.default_payment_terms_days }) : null} />
            </Grid>

            {/* Settings */}
            <Typography variant="subtitle2" sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'text.secondary', fontSize: '12px' }}>
              {t('vadmin.basicInfo')}
            </Typography>
            <Grid container spacing={1.5} sx={{ mb: 3 }}>
              <InfoItem label={t('vadmin.orgId')} value={org.id} />
              <InfoItem label={t('vadmin.language')} value={LANGUAGE_LABELS[org.default_language] ?? org.default_language} icon={<LanguageIcon fontSize="small" />} />
              <InfoItem label={t('vadmin.legacyPricing')} value={org.has_legacy_pricing ? t('vadmin.enabled') : t('vadmin.disabled')} />
              <InfoItem label={t('vadmin.created')} value={formatDate(org.created_at)} />
            </Grid>

            {/* Features */}
            <Typography variant="subtitle2" sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'text.secondary', fontSize: '12px' }}>
              {t('vadmin.features')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              <FeatureChip label="Maventa" enabled={org.maventa_enabled} t={t} />
              <FeatureChip label={t('inventory.title')} enabled={org.inventory_tracking_enabled} t={t} />
              <FeatureChip label={t('nav.workTime')} enabled={org.work_hours_reporting_enabled} t={t} />
              <FeatureChip label={t('nav.aiPhone')} enabled={org.ai_phone_enabled} t={t} />
              <FeatureChip label={t('tire_hotel.hotel_cards')} enabled={org.tire_hotel_enabled} t={t} />
              <FeatureChip label={t('booking.bookings')} enabled={org.booking_beta} t={t} />
              <FeatureChip label={t('googleCalendar.title')} enabled={org.google_calendar_beta_enabled} t={t} />
              <FeatureChip label={t('nav.serviceBays')} enabled={org.service_bays_enabled} t={t} />
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Users */}
            <Typography variant="subtitle2" sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'text.secondary', fontSize: '12px' }}>
              {t('vadmin.usersAndRoles')} ({org.users?.length || 0})
            </Typography>
            {(!org.users || org.users.length === 0) ? (
              <Typography variant="body2" color="text.secondary">{t('vadmin.noUsers')}</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {org.users
                  .sort((a, b) => (ROLE_ORDER[a.role] ?? 9) - (ROLE_ORDER[b.role] ?? 9))
                  .map((user) => (
                  <Paper
                    key={user.id}
                    variant="outlined"
                    sx={{ p: 1.5 }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '8px',
                          bgcolor: 'rgba(49, 130, 206, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'secondary.main',
                          flexShrink: 0,
                        }}
                      >
                        <PersonIcon fontSize="small" />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {user.name || user.email}
                          </Typography>
                          <Chip
                            label={getRoleLabel(user.role)}
                            size="small"
                            color={getRoleColor(user.role)}
                            variant="outlined"
                            sx={{ fontSize: '11px', height: 22 }}
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                          {user.email}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexShrink: 0 }}>
                        <Box>
                          <Typography variant="caption" color="text.disabled" display="block">{t('vadmin.lastLogin')}</Typography>
                          <Typography variant="caption" fontWeight={500}>{formatDateTime(user.last_login)}</Typography>
                        </Box>
                        {currentUser?.is_superuser && (
                          <Tooltip title={t('vadmin.impersonate')}>
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => { window.location.href = `/api/impersonate/${user.id}/`; }}
                            >
                              <LoginIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}
            </Box>
            )}

            {activeTab === 1 && (
              <>
                <CancelSubscriptionPanel
                  organizationId={organizationId}
                  onChanged={refreshOrg}
                />
                {/* While a cancellation is pending the subscription is still ACTIVE,
                    so the start form would report "already has an active invoice
                    subscription" and 400. Hide it until the cancellation is undone. */}
                {!hasPendingCancellation && (
                  <StartInvoiceSubscriptionForm
                    organizationId={organizationId}
                    onStarted={refreshOrg}
                  />
                )}
                <DataPurgePanel org={org} onChanged={refreshOrg} />
              </>
            )}

            {activeTab === 2 && (
              <FeatureFlagsPanel organizationId={organizationId} onChanged={refreshOrg} />
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined">
          {t('Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
